import { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../i18n/I18nProvider";
import { toast } from "sonner";
import { Mail, Lock, User, ArrowRight, ArrowLeft, Shield } from "lucide-react";
import { logger } from "../lib/logger";

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

function formatErr(detail) {
  if (!detail) return null;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" · ");
  return detail.msg || String(detail);
}

function GoogleLogo() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#EA4335" d="M12 10.2v3.9h5.5c-.2 1.4-1.6 4.1-5.5 4.1-3.3 0-6-2.7-6-6.2s2.7-6.2 6-6.2c1.9 0 3.1.8 3.8 1.5l2.6-2.5C16.8 3.4 14.6 2.4 12 2.4 6.7 2.4 2.5 6.7 2.5 12s4.2 9.6 9.5 9.6c5.5 0 9.1-3.9 9.1-9.3 0-.6-.1-1.1-.2-1.6H12z" />
    </svg>
  );
}

export default function Login() {
  const { t } = useI18n();
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // step: "choose" → two side-by-side buttons; "email" → email form
  const [step, setStep] = useState("choose");
  const [mode, setMode] = useState("login"); // "login" | "register"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");

  // Honour ?invite_email=... and ?reset_token=... query params
  useEffect(() => {
    const inviteEmail = searchParams.get("invite_email");
    const resetToken = searchParams.get("reset_token");
    if (resetToken) {
      navigate(`/forgot-password?reset_token=${resetToken}`, { replace: true });
      return;
    }
    if (inviteEmail) {
      setStep("email");
      setMode("login");
      setEmail(inviteEmail);
    }
  }, [searchParams, navigate]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [turnstileToken, setTurnstileToken] = useState("");
  const turnstileRef = useRef(null);
  const widgetIdRef = useRef(null);

  // Load Turnstile script (once) when register tab is active and key is configured
  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) return;
    if (mode !== "register" || step !== "email") return;
    if (document.querySelector('script[data-turnstile]')) return;
    const s = document.createElement("script");
    s.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    s.async = true;
    s.defer = true;
    s.dataset.turnstile = "true";
    document.head.appendChild(s);
  }, [mode, step]);

  // Render widget when DOM target is ready and library is loaded
  useEffect(() => {
    if (!TURNSTILE_SITE_KEY || mode !== "register" || step !== "email") return;
    let interval = null;
    const tryRender = () => {
      if (!turnstileRef.current || !window.turnstile) return false;
      if (widgetIdRef.current !== null) return true;
      try {
        widgetIdRef.current = window.turnstile.render(turnstileRef.current, {
          sitekey: TURNSTILE_SITE_KEY,
          theme: "light",
          callback: (tok) => setTurnstileToken(tok),
          "error-callback": () => setTurnstileToken(""),
          "expired-callback": () => setTurnstileToken(""),
        });
        return true;
      } catch (e) {
        return false;
      }
    };
    if (!tryRender()) {
      interval = setInterval(() => { if (tryRender()) clearInterval(interval); }, 250);
    }
    return () => {
      if (interval) clearInterval(interval);
      if (window.turnstile && widgetIdRef.current !== null) {
        try { window.turnstile.remove(widgetIdRef.current); } catch (e) { logger.warn("Turnstile remove failed", e); }
        widgetIdRef.current = null;
      }
    };
  }, [mode, step]);

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      const path = mode === "login" ? "/auth/login" : "/auth/register";
      let body;
      if (mode === "login") {
        body = { email, password };
      } else {
        if (TURNSTILE_SITE_KEY && !turnstileToken) {
          setErr(t("login.turnstile.required"));
          setBusy(false);
          return;
        }
        body = { email, password, name };
        if (turnstileToken) body.turnstile_token = turnstileToken;
      }
      const { data } = await api.post(path, body);
      setUser(data);
      toast.success(t(mode === "login" ? "login.welcomeBack" : "login.created"));
      navigate("/dashboard", { replace: true });
    } catch (e2) {
      setErr(formatErr(e2.response?.data?.detail) || e2.message);
      // Reset Turnstile so user can retry
      if (window.turnstile && widgetIdRef.current !== null) {
        try { window.turnstile.reset(widgetIdRef.current); } catch (e) { logger.warn("Turnstile reset failed", e); }
        setTurnstileToken("");
      }
    } finally {
      setBusy(false);
    }
  };

  const googleLogin = () => {
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-[var(--bone)] grid place-items-center px-6 py-10" data-testid="login-page">
      <div className="w-full max-w-2xl border border-[var(--ink-200)] bg-[var(--surface)] p-8 md:p-10">
        <Link to="/" className="label-tech hover:text-[var(--ink)] inline-flex items-center gap-1">
          <ArrowLeft size={12} /> {t("detail.back")}
        </Link>
        <h1 className="font-display text-3xl md:text-4xl mt-3 tracking-tight">
          {step === "choose" ? t("login.chooseTitle") : (mode === "login" ? t("login.title") : t("login.registerTitle"))}
        </h1>
        <p className="label-tech mt-2">
          {step === "choose" ? t("login.chooseSubtitle") : t("login.subtitle")}
        </p>

        {step === "choose" && (
          <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="login-method-grid">
            <button
              type="button"
              onClick={googleLogin}
              className="group flex flex-col items-start text-left gap-3 border border-[var(--ink-200)] bg-[var(--bone)] hover:bg-[var(--surface-alt)] hover:border-[var(--ink)] transition-colors p-6 min-h-[180px]"
              data-testid="login-method-google"
            >
              <div className="w-10 h-10 bg-[var(--surface-alt)] grid place-items-center border border-[var(--ink-200)]">
                <GoogleLogo />
              </div>
              <div className="font-display text-lg leading-tight">{t("login.googleTitle")}</div>
              <div className="label-tech">{t("login.googleHint")}</div>
              <div className="mt-auto label-tech inline-flex items-center gap-1 group-hover:text-[var(--ink)]">
                {t("login.continue")} <ArrowRight size={12} />
              </div>
            </button>

            <button
              type="button"
              onClick={() => { setStep("email"); setMode("login"); setErr(null); }}
              className="group flex flex-col items-start text-left gap-3 border border-[var(--ink-200)] bg-[var(--bone)] hover:bg-[var(--surface-alt)] hover:border-[var(--ink)] transition-colors p-6 min-h-[180px]"
              data-testid="login-method-email"
            >
              <div className="w-10 h-10 bg-[var(--surface-alt)] grid place-items-center border border-[var(--ink-200)]">
                <Mail size={18} />
              </div>
              <div className="font-display text-lg leading-tight">{t("login.emailTitle")}</div>
              <div className="label-tech">{t("login.emailHint")}</div>
              <div className="mt-auto label-tech inline-flex items-center gap-1 group-hover:text-[var(--ink)]">
                {t("login.continue")} <ArrowRight size={12} />
              </div>
            </button>
          </div>
        )}

        {step === "email" && (
          <>
            <button
              type="button"
              onClick={() => { setStep("choose"); setErr(null); }}
              className="label-tech mt-4 inline-flex items-center gap-1 hover:text-[var(--ink)]"
              data-testid="login-back-btn"
            >
              <ArrowLeft size={12} /> {t("login.backToMethods")}
            </button>

            <div className="mt-6 flex border border-[var(--ink-200)]">
              <button
                type="button"
                onClick={() => { setMode("login"); setErr(null); }}
                className={`flex-1 py-2 text-xs font-mono-tech uppercase tracking-[0.1em] ${mode === "login" ? "bg-[var(--ink)] text-[var(--bone)]" : "bg-[var(--bone)]"}`}
                data-testid="login-tab-login"
              >
                {t("login.tabLogin")}
              </button>
              <button
                type="button"
                onClick={() => { setMode("register"); setErr(null); }}
                className={`flex-1 py-2 text-xs font-mono-tech uppercase tracking-[0.1em] ${mode === "register" ? "bg-[var(--ink)] text-[var(--bone)]" : "bg-[var(--bone)]"}`}
                data-testid="login-tab-register"
              >
                {t("login.tabRegister")}
              </button>
            </div>

            <form onSubmit={submit} className="mt-6 space-y-4" data-testid="login-form">
              {mode === "register" && (
                <div>
                  <div className="label-tech mb-1 inline-flex items-center gap-1"><User size={12} /> {t("login.name")}</div>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="input-tech"
                    placeholder="Anna Andersson"
                    data-testid="login-name-input"
                  />
                </div>
              )}
              <div>
                <div className="label-tech mb-1 inline-flex items-center gap-1"><Mail size={12} /> {t("login.email")}</div>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input-tech"
                  placeholder="namn@foretag.se"
                  data-testid="login-email-input"
                  autoComplete="email"
                />
              </div>
              <div>
                <div className="label-tech mb-1 inline-flex items-center gap-1"><Lock size={12} /> {t("login.password")}</div>
                <input
                  type="password"
                  required
                  minLength={mode === "register" ? 8 : 1}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input-tech"
                  placeholder={mode === "register" ? t("login.passwordHint") : ""}
                  data-testid="login-password-input"
                  autoComplete={mode === "login" ? "current-password" : "new-password"}
                />
              </div>

              {err && (
                <div className="border border-[var(--brick)] bg-[var(--brick)]/10 text-[var(--brick)] p-3 text-sm" data-testid="login-error">
                  {err}
                </div>
              )}

              {mode === "register" && TURNSTILE_SITE_KEY && (
                <div>
                  <div className="label-tech mb-2 inline-flex items-center gap-1"><Shield size={12} /> {t("login.turnstile.label")}</div>
                  <div ref={turnstileRef} data-testid="turnstile-widget" />
                </div>
              )}

              <button type="submit" disabled={busy} className="btn-primary w-full justify-center py-3" data-testid="login-submit-btn">
                {busy ? "…" : (mode === "login" ? t("login.submit") : t("login.registerSubmit"))} <ArrowRight size={14} />
              </button>

              {mode === "login" && (
                <div className="text-center pt-1">
                  <Link to="/forgot-password" className="label-tech hover:text-[var(--ink)] underline" data-testid="login-forgot-link">
                    {t("login.forgotPassword")}
                  </Link>
                </div>
              )}
            </form>
          </>
        )}
      </div>
    </div>
  );
}
